// FludeeProductPage.jsx
// Premium floating cards (no border)
// Layout: Desktop = 5 | Tablet = 2,2,1 | Mobile = 1
// Subtle animation + inner glow

import * as React from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Grid,
  Paper,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import StarsOutlinedIcon from "@mui/icons-material/StarsOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";

import bck from "../assets/backtgch.png";
const HERO_BG = bck;

const DATA = [
 /* {
    title: "Unmet Clinical Need",
    icon: <LocalHospitalOutlinedIcon />,
    items: [
      "Fragmented device + data systems in wards and clinics",
      "Manual documentation workload for clinicians",
      "Delayed insight for early deterioration detection",
      "Limited traceability and audit-ready reporting",
    ],
  },*/
  {
  title: "System Architecture",
  icon: <AccountTreeOutlinedIcon />,
  items: [
    "Physiological data capture with validation logic",
    "Secure ingestion and clinical data pipeline",
    "Electrolyte risk stratification engine ",
    "Clinician-facing dashboards and decision views",
    "Compliance-ready audit trails and access control",
  ],
},

 /* {
    title: "Key Features",
    icon: <StarsOutlinedIcon />,
    items: [
      "Workflow-first clinical design",
      "Near real-time trends with reduced alert noise",
      "Operational and clinical KPIs (planned)",
      "FHIR-aligned interoperability (planned)",
    ],
  },*/
 {
  title: "Use Cases",
  icon: <HubOutlinedIcon />,
  items: [
    "Early identification of fluid–electrolyte imbalance",
    "Ward-level monitoring and triage prioritization",
    "Continuous screening using connected sensing data",
    "Clinical validation and feasibility studies",
    "Longitudinal data capture for outcome analysis",
  ],
},

  {
  title: "Differentiation",
  icon: <InsightsOutlinedIcon />,
  items: [
    "Clinically embedded design philosophy",
    "Electrolyte-centric data intelligence",
    "Signal fidelity over alert volume",
    "Deployment-ready across care settings",
    "Regulatory foresight by design",
  ],
}

];

function BulletList({ items }) {
  return (
    <List dense sx={{ py: 0 }}>
      {items.map((t, i) => (
        <ListItem key={i} disableGutters sx={{ px: 0, alignItems: "flex-start" }}>
          <ListItemIcon sx={{ minWidth: 28, mt: "3px" }}>
            <CheckCircleOutlineOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t}
            primaryTypographyProps={{
              sx: { fontSize: 14, lineHeight: 1.55 },
            }}
          />
        </ListItem>
      ))}
    </List>
  );
}

function FeatureCard({ icon, title, items, delayMs = 0 }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        height: "100%",
        p: 3,
        borderRadius: 4,
        position: "relative",
        overflow: "hidden",
        border: "none",
        bgcolor: alpha(theme.palette.background.paper, isDark ? 0.92 : 1),

        display: "flex",
        flexDirection: "column",
        minHeight: 260,

        boxShadow: isDark
          ? `0 12px 40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04)`
          : `0 14px 45px rgba(15, 23, 42, 0.12), inset 0 1px 0 rgba(255,255,255,0.9)`,

        opacity: 0,
        transform: "translateY(16px)",
        animation: "fadeUp 700ms ease forwards",
        animationDelay: `${delayMs}ms`,

        transition: "transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease",

        // ✅ hover lift little (big lift = overlap)
        "&:hover": {
          transform: { xs: "none", md: "translateY(-2px)" }, // -3 -> -2 ✅
          zIndex: 2,
          boxShadow: isDark
            ? `0 20px 70px rgba(0,0,0,0.75), inset 0 1px 0 rgba(255,255,255,0.06)`
            : `0 22px 70px rgba(15, 23, 42, 0.18), inset 0 1px 0 rgba(255,255,255,1)`,
        },

        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `radial-gradient(
            500px 200px at 20% 0%,
            ${alpha(theme.palette.primary.main, isDark ? 0.22 : 0.12)},
            transparent 65%
          )`,
          pointerEvents: "none",
        },

        "@keyframes fadeUp": {
          from: { opacity: 0, transform: "translateY(16px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 3,
            display: "grid",
            placeItems: "center",
            color: "primary.main",
            bgcolor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.12),
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontWeight: 900, fontSize: 18 }}>{title}</Typography>
      </Stack>

      <Divider sx={{ my: 1.6, opacity: 0.6 }} />
      <BulletList items={items} />
    </Paper>
  );
}

export default function FludeeProductPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* HERO */}
      <Box
        sx={{
          position: "relative",
          py: { xs: 5, md: 8 },
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: isDark ? alpha("#000", 0.75) : alpha("#fff", 0.85),
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative" }}>
          <Stack spacing={2} maxWidth={820}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label="Flagship Platform" color="primary" />
              <Chip icon={<ShieldOutlinedIcon />} label="Secure by design" variant="outlined" />
            </Stack>

            <Typography component="h1" sx={{ fontWeight: 950, fontSize: "clamp(30px,5vw,54px)" }}>
              FLUDEE{" "}
              <Box component="span" sx={{ color: "primary.main" }}>
              Unmet Clinical Need
              </Box>
            </Typography>

            <Typography sx={{ color: "text.secondary", fontSize: 15 }}>
              FLUDEE addresses a critical unmet clinical need created by the absence of continuous, objective, and integrated fluid intelligence in hospital care. Fluid balance and electrolyte status are still managed through manual records and intermittent measurements, leaving clinicians blind to early physiological deterioration. This data gap drives delayed detection, variable decisions, preventable complications, and avoidable clinical risk.
             </Typography>
          </Stack>
        </Container>
      </Box>

      {/* CONTENT */}
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
        <Stack alignItems="center" textAlign="center" sx={{ mb: 4 }}>
          <Typography sx={{ fontWeight: 950, fontSize: 28 }}>Hospital-ready by design</Typography>
          <Typography sx={{ color: "text.secondary", maxWidth: 900 }}>
           Fludee is engineered to align with real clinical environments, supporting structured workflows, clear data traceability, and dependable system behavior. The platform is designed with scalability in mind, allowing it to adapt from pilot use to wider hospital deployment while maintaining consistency, reliability, and operational clarity.
          </Typography>
        </Stack>

        <Grid
          container
          justifyContent="center"
          columnSpacing={{ xs: 2, sm: 2.5, md: 3, lg: 3 }}
          rowSpacing={{ xs: 2.6, sm: 3.2, md: 3.6, lg: 4 }}
          sx={{
            alignItems: "stretch",
            overflow: "visible", // ✅ allow shadow safely
          }}
        >
          {DATA.map((c, i) => (
            <Grid
              key={c.title}
              item
              xs={12}
              sm={6}
              md={6}
              lg={2.4}
              sx={{ display: "flex" }}
            >
              {/* ✅ MAIN FIX: Shadow buffer so next row never overlaps */}
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  pb: { xs: 2.2, sm: 2.6, md: 3, lg: 3.4 }, // ✅ extra bottom space for shadow
                }}
              >
                <FeatureCard {...c} delayMs={i * 90} />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
